import {createRecipePlan} from '../recipes/recipe-process.js';
import {
  PlannerBlockKind,
  PlannerDurationKind,
  PlannerSourceKind,
  createPlannerSource
} from './schedule-model.js';

function required(value,label,max=180){
  const text=String(value??'').trim();
  if(!text)throw new TypeError(label+' is required');
  if(text.length>max)throw new TypeError(label+' is too long');
  return text;
}

function hasText(value){
  return String(value??'').trim().length>0;
}

function blockDuration(value){
  return value==null
    ?Object.freeze({durationMinutes:null,durationKind:PlannerDurationKind.MISSING})
    :Object.freeze({durationMinutes:Number(value),durationKind:PlannerDurationKind.FIXED});
}

function preparationBlocks({
  scope,
  itemId,
  itemName,
  preparation
}={}){
  if(!preparation)return Object.freeze([]);
  const prefix=scope+':'+itemId;
  const result=[];

  const hasPreparation=preparation.preparationDurationMinutes!=null
    ||hasText(preparation.preparationNotes)
    ||hasText(preparation.marinadeNotes)
    ||hasText(preparation.spicesNotes);
  if(hasPreparation){
    const duration=blockDuration(preparation.preparationDurationMinutes);
    result.push({
      id:prefix+':preparation',
      kind:PlannerBlockKind.PREPARATION,
      label:'Przygotowanie · '+itemName,
      ...duration,
      sourceRef:{
        kind:scope+'_preparation',
        id:itemId,
        field:'preparationDurationMinutes'
      },
      sequence:0,
      itemId
    });
  }

  const hasCuring=preparation.curingDurationMinutes!=null
    ||hasText(preparation.curingMethodKey)
    ||hasText(preparation.curingMethodCustom)
    ||hasText(preparation.curingNotes);
  if(hasCuring){
    const duration=blockDuration(preparation.curingDurationMinutes);
    result.push({
      id:prefix+':curing',
      kind:PlannerBlockKind.CURING,
      label:'Peklowanie / marynowanie · '+itemName,
      ...duration,
      sourceRef:{
        kind:scope+'_preparation',
        id:itemId,
        field:'curingDurationMinutes'
      },
      sequence:1,
      itemId
    });
  }

  const hasDrying=preparation.dryingDurationMinutes!=null||hasText(preparation.dryingNotes);
  if(hasDrying){
    const duration=blockDuration(preparation.dryingDurationMinutes);
    result.push({
      id:prefix+':drying',
      kind:PlannerBlockKind.DRYING,
      label:'Osuszanie · '+itemName,
      ...duration,
      sourceRef:{
        kind:scope+'_preparation',
        id:itemId,
        field:'dryingDurationMinutes'
      },
      sequence:2,
      itemId
    });
  }

  return Object.freeze(result);
}

function stageBlock({scope,stage,target,index}={}){
  const duration=blockDuration(target?.plannedDurationMinutes??null);
  return Object.freeze({
    id:scope+':stage:'+required(stage.id,'stage.id',180),
    kind:PlannerBlockKind.STAGE,
    label:stage.nameOverride||stage.typeKey,
    ...duration,
    sourceRef:{
      kind:scope+'_stage',
      id:stage.id,
      field:'plannedDurationMinutes'
    },
    sequence:Number(stage.sortOrder??index??0),
    stageType:stage.typeKey
  });
}

export function plannerSourceFromRecipe(plan){
  const value=createRecipePlan(plan);
  const preparations=new Map(
    value.preparations.map(preparation=>[preparation.recipeItemId,preparation])
  );
  const targets=new Map(
    value.stageTargets.map(target=>[target.recipeStageId,target])
  );

  return createPlannerSource({
    kind:PlannerSourceKind.RECIPE,
    id:value.recipe.id,
    itemChains:value.items.map(item=>({
      itemId:item.id,
      itemName:item.name,
      blocks:preparationBlocks({
        scope:'recipe',
        itemId:item.id,
        itemName:item.name,
        preparation:preparations.get(item.id)||null
      })
    })),
    processBlocks:value.stages.map((stage,index)=>stageBlock({
      scope:'recipe',
      stage,
      target:targets.get(stage.id)||null,
      index
    }))
  });
}

export function plannerSourceFromSessionBundle(bundle={}){
  const session=bundle.session;
  if(!session?.id)throw new TypeError('Session bundle is required');
  const items=(bundle.items||[]).filter(item=>!item.deletedAt);
  const stages=(bundle.stages||[]).filter(stage=>!stage.deletedAt);
  if(!items.length)throw new TypeError('Planner session source requires at least one item');
  if(!stages.length)throw new TypeError('Planner session source requires at least one stage');

  const preparations=new Map(
    (bundle.preparations||[]).map(preparation=>[preparation.itemId,preparation])
  );
  const targets=new Map(
    (bundle.stageTargets||[]).map(target=>[target.stageId,target])
  );

  return createPlannerSource({
    kind:PlannerSourceKind.SESSION,
    id:session.id,
    itemChains:items.map(item=>({
      itemId:item.id,
      itemName:item.nameSnapshot||'Produkt',
      blocks:preparationBlocks({
        scope:'session',
        itemId:item.id,
        itemName:item.nameSnapshot||'Produkt',
        preparation:preparations.get(item.id)||null
      })
    })),
    processBlocks:stages.map((stage,index)=>stageBlock({
      scope:'session',
      stage,
      target:targets.get(stage.id)||null,
      index
    }))
  });
}
